// Copyright (c) 2024 Siemens

/**
 * @module js/importDataUploadService
 */

import AwPromiseService from 'js/awPromiseService';
import { get, isNil } from 'lodash';
import { publish } from 'js/accEventBus';
import dataImportService from 'js/dataImportService';
import messagingService from 'js/messagingService';
import AccHttpService from 'js/accHttpService';
const importDataUploadService = {};


/**
 * Uploads and imports selected files into collab
 *
 * @param {*} files
 * @param {*} props
 * @param {*} createStagingAreaObject
 * @param {*} viewModel
 * @returns
 */
importDataUploadService.uploadFiles = async function (files, props, createStagingAreaObject, viewModel) {
    const deferred = AwPromiseService.instance.defer();
    try {
        // Ensure resource must be free
        if (dataImportService.isResourceBusy() === false) {
            await dataImportService.initImport(files, props, createStagingAreaObject, viewModel);
        }
        deferred.resolve(true);
    } catch (err) {
        deferred.reject(err);
    }
    return deferred.promise;
};

/**
 * Get selected file info
 *
 * @param {*} parameters params
 * @returns promise
 */
importDataUploadService.getSelectedFileInfo = (parameters, pendingStatus) => {
    const deferred = AwPromiseService.instance.defer();
    if (parameters.formData) {
        for (const fileEle of parameters.formData) {
            fileEle.file.status = pendingStatus;
            dispatchFileStatus(fileEle.file);
        }
    }
    deferred.resolve({
        dataToBeRelated: {
            attachFiles: parameters.fileName
        },
        fileInputForms: parameters.formData
    });

    return deferred.promise;
};

const dispatchFileStatus = (file) => {
    publish('des-import-data.uploadStatus', { fileName: file['name'], status: file['status'] });
};

importDataUploadService.subscribeEvents = (toggle, vmo) => {
    const eventName = 'des-import-data.metadata-present';
    if (toggle) {
        const { data, dispatch } = vmo;
        window.addEventListener(eventName, (eventData) => {
            importDataUploadService.metaDataEventHandler(eventName, eventData, vmo);
        });
    } else {
        window.removeEventListener(eventName, () => {
            console.log('metadata present event has been removed from acc object');
        });
    }
}

importDataUploadService.registerFileUploadWorker = async (vmo, props) => {
    try {
        // Check resource busy
        if (dataImportService.isResourceBusy()) {
            messagingService.showWarning( vmo.i18n.resourceBusyWarning );
        } else {
            const { createStagingArea, hasErrors } = await importDataUploadService.createStagingArea(vmo, props);
            if (!hasErrors) {
                // Process file upload.
                importDataUploadService.uploadFiles(get(vmo, 'data.fileInputForms'), props, createStagingArea, vmo).then(() => {
                    // show success
                    messagingService.showInfo( vmo.i18n.fileUploadSuccess );
                    vmo.dispatch({ path: 'data.uploadStatus', value: 'COMPLETED' });
                }).catch((err) => {
                    importDataUploadService.showError(vmo, err);
                });
            }
        }
    } catch (err) {
        importDataUploadService.showError(vmo, err);
    }
}

importDataUploadService.createStagingArea = async (vmo, props) => {
    // Dispatch acc close event
    publish('des-import-data.autoClose', true);
    // Disable button
    vmo.dispatch({ path: 'data.uploadStatus', value: 'INIT' });
    // Make http request
    const output = await AccHttpService.createHttpRequest(
        {
            method: 'POST',
            url: '/acc/orchestrate',
            withCredentials: true,
            data: {
                additionalProperties: {},
                query: `
                    mutation CreateStagingArea($createStagingAreaInput: CreateStagingAreaInput) {
                        createStagingArea(createStagingAreaInput: $createStagingAreaInput)
                    }
                `,
                variables: {
                    createStagingAreaInput: {
                        attachFiles: get(vmo, 'data.dataToBeRelated.attachFiles'),
                        collaborationspaceId: get(props, 'collaborationspaceid'),
                        containerId: get(props, 'containerid'),
                        containerType: get(props, 'containertype'),
                        folderId: get(props, 'folderid'),
                        stagingAreaType: get(props, 'stagingareatype'),
                        metadata: get(vmo, 'data.metadata')
                    }
                }
            }
        },
        'collab',
        get(props, 'accsessionid')
    );
    const createStagingArea = get(output, 'data.data.createStagingArea');
    // Check errors if any
    const errors = get(createStagingArea, 'errors');
    let hasErrors = errors && Array.isArray(errors) && errors.length;
    if ( hasErrors ) {
        importDataUploadService.showError( vmo, errors );
    }

    return { createStagingArea, hasErrors };
}

importDataUploadService.showError = (vmo, eventData) => {
    messagingService.showError(get(vmo, 'i18n.failedToUploadFiles'));
    if (eventData) {
        publish('des-import-data.failed', eventData);
    }
    vmo.dispatch({ path: 'data.uploadStatus', value: 'FAILED' });
}

importDataUploadService.parseMetaData = (detail, vmo) => {
    let metaData = detail;
    try {
        if (typeof metaData === 'string') {
            metaData = JSON.parse(detail);
        }
        vmo.dispatch({ path: 'data.uploadStatus', value: get(vmo, 'data.uploadStatus') });
    } catch (err) {
        messagingService.showError(`${get(vmo, 'i18n.metaDataReadError')} "${eventName}"`);
        vmo.dispatch({ path: 'data.uploadStatus', value: get(vmo, 'data.statusMap.ERROR') });
    }
    return metaData;
}
importDataUploadService.metaDataEventHandler = (eventName, eventData, vmo) => {
    const detail = get(eventData, 'detail');
    if (isNil(detail)) {
        messagingService.showError(`${eventName}: ${get(vmo, 'i18n.emptyEventDataFound')}`);
    } else {
        vmo.data['metadata'] = importDataUploadService.parseMetaData(detail, vmo);
        console.log('metadata is:', vmo.data['metadata']);
        vmo.dispatch({ path: 'data.metadata', value: vmo.data['metadata'] });  
    }
}
export default importDataUploadService;
